import React, { useState, useEffect } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'

const MotionLink = motion(Link)

const TYPING_TEXT = 'CREATIVE DEVELOPER'
const TYPING_SPEED = 80

export default function Hero() {
  const [displayedText, setDisplayedText] = useState('')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  useEffect(() => {
    let timeout
    if (displayedText.length < TYPING_TEXT.length) {
      timeout = setTimeout(() => {
        setDisplayedText(TYPING_TEXT.slice(0, displayedText.length + 1))
      }, TYPING_SPEED)
    }
    return () => clearTimeout(timeout)
  }, [displayedText])

  const { scrollY } = useScroll()
  const contentY = useTransform(scrollY, [0, 600], [0, 120])
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0])

  return (
    <section
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: isMobile ? '80px 1rem 4rem' : '100px 2rem 5rem',
      }}
    >

      <motion.div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
          y: contentY,
          opacity: contentOpacity,
        }}
      >
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
          style={{
            fontSize: isMobile ? 'clamp(3.5rem, 18vw, 5rem)' : 'clamp(5rem, 16vw, 14rem)',
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: '-0.03em',
            lineHeight: 0.85,
            background: 'linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.75) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 40px rgba(0,217,255,0.25))',
            margin: 0,
            userSelect: 'none'
          }}
        >
          ROHAN<br />DALVI
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
          style={{
            marginTop: '2rem',
            fontSize: '0.8rem',
            letterSpacing: isMobile ? '0.15em' : '0.25em',
            textTransform: 'uppercase',
            color: '#00d9ff',
            fontFamily: 'monospace'
          }}
        >
          {displayedText}
          <span style={{ animation: 'blink 1s step-end infinite' }}>_</span>
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6, ease: 'easeOut' }}
          style={{
            marginTop: '0.75rem',
            fontSize: isMobile ? '0.85rem' : '0.95rem',
            color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.05em'
          }}
        >
          Self-taught. Still in school. Already shipping.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          style={{
            marginTop: '2.5rem',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {[
            { label: 'View Projects', href: '/projects', filled: false },
            { label: 'Play Games', href: '/games', filled: false },
            { label: 'About Me', href: '/about', filled: true },
          ].map(btn => (
            <MotionLink
              key={btn.href}
              to={btn.href}
              whileHover={{ scale: 1.05, background: btn.filled ? '#00c4e0' : 'rgba(0,217,255,0.12)' }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.15 }}
              style={{
                padding: '10px 24px',
                borderRadius: '6px',
                border: `1px solid ${btn.filled ? '#00d9ff' : 'rgba(0,217,255,0.5)'}`,
                color: btn.filled ? '#050508' : '#00d9ff',
                textDecoration: 'none',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                background: btn.filled ? '#00d9ff' : 'transparent',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '280px' : 'none',
                textAlign: 'center',
                display: 'inline-block',
              }}
            >{btn.label}</MotionLink>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        style={{
          position: 'absolute',
          bottom: '2rem',
          left: '2rem',
          zIndex: 1,
          display: isMobile ? 'none' : 'flex',
        }}
      >
        {['DEVELOPER·', 'CREATOR·', 'INNOVATOR·'].map(word => (
          <motion.span
            key={word}
            whileHover={{ color: '#00d9ff', textShadow: '0 0 15px #00d9ff' }}
            transition={{ duration: 0.15 }}
            style={{
              fontSize: '0.65rem',
              letterSpacing: '0.2em',
              color: 'rgba(255,255,255,0.2)',
              textTransform: 'uppercase',
              fontFamily: 'monospace',
              cursor: 'default',
            }}
          >{word}</motion.span>
        ))}
      </motion.div>
    </section>
  )
}
